import { getIntlLocale } from "@/lib/locale";

// Plan prices shown in the UI. Checkout charges what Stripe holds, so these only describe it.
// The sale is a first-year coupon applied at checkout, and renewals go back to the list price.
// The backend applies the same sale from pythonbackend/core/pricing.py. Change both together.
const FOUNDING_PRICES = { monthly: 3, yearly: 30 };
const STANDARD_PRICES = { monthly: 7, yearly: 70 };
export const SALE_YEARLY_PRICE = 40;

// Sunday 1 Nov 2026, 23:59:59 in Lisbon. Clocks are back on WET (UTC+0) by then.
export const SALE_ENDS_AT = "2026-11-01T23:59:59Z";

export function isSaleActive(now: number = Date.now()): boolean {
  return now <= Date.parse(SALE_ENDS_AT);
}

export interface PlanPrices {
  monthly: number;
  /** What the yearly plan costs for the first year, so the sale price while a sale runs. */
  yearly: number;
  /** The regular yearly price, also what a sale year renews at. */
  yearlyList: number;
  onSale: boolean;
  /** How much the yearly plan saves against twelve monthly payments, rounded to a whole percent. */
  yearlySavingsPercent: number;
  /** How much the sale takes off the regular yearly price, 0 when there is no sale. */
  salePercentOff: number;
}

/** Founders keep their own price and never get the sale. */
export function getPlanPrices(
  isFounding: boolean,
  now: number = Date.now(),
): PlanPrices {
  const list = isFounding ? FOUNDING_PRICES : STANDARD_PRICES;
  const onSale = !isFounding && isSaleActive(now);
  const yearly = onSale ? SALE_YEARLY_PRICE : list.yearly;
  return {
    monthly: list.monthly,
    yearly,
    yearlyList: list.yearly,
    onSale,
    yearlySavingsPercent: Math.round((1 - yearly / (list.monthly * 12)) * 100),
    salePercentOff: Math.round((1 - yearly / list.yearly) * 100),
  };
}

export interface ReferralCredit {
  /** What the banked free months take off the first yearly payment. */
  yearlyCredit: number;
  /** The first yearly payment once the credit is taken off. */
  yearlyAfterCredit: number;
}

/**
 * What banked referral months take off a yearly checkout. Each month is 1/12th of the first
 * year's price, so of the sale price while a sale runs, and the payment never goes below 0. The
 * backend builds the same discount (_build_referral_coupon and _build_sale_coupon in
 * api/routers/billing.py).
 */
export function getReferralCredit(
  prices: PlanPrices,
  freeMonths: number,
): ReferralCredit {
  const yearlyCredit = Math.min(
    prices.yearly,
    freeMonths * (prices.yearly / 12),
  );
  return { yearlyCredit, yearlyAfterCredit: prices.yearly - yearlyCredit };
}

// A sale checkout keeps the remaining app trial only when at least this much is left. The backend
// applies the same rule (MIN_CARRIED_TRIAL in pythonbackend/core/pricing.py).
const MIN_CARRIED_TRIAL_MS = 49 * 60 * 60 * 1000;

/**
 * When a sale checkout will start charging, if it keeps the user's remaining trial. Null when the
 * checkout charges straight away: no sale, no trial, or too little trial left.
 */
export function carriedTrialEnd(
  isOnSale: boolean,
  tier: "paid" | "trial" | "free",
  trialEndsAt: string | null,
  now: number = Date.now(),
): Date | null {
  if (!isOnSale || tier !== "trial" || !trialEndsAt) return null;
  const end = Date.parse(trialEndsAt);
  return end - now >= MIN_CARRIED_TRIAL_MS ? new Date(end) : null;
}

/** The sale's last day, as a date in the active language ("1 November"). */
export function formatSaleEndDate(): string {
  return new Date(SALE_ENDS_AT).toLocaleDateString(getIntlLocale(undefined), {
    day: "numeric",
    month: "long",
    timeZone: "Europe/Lisbon",
  });
}
